/* eslint-disable react/function-component-definition */
import { useCallback, useRef, useState } from "react";
import api from "api/api";

const parseNumber = (value) => {
  if (!value) return 0;
  return Number(String(value).replace(/,/g, "")) || 0;
};

const formatNumber = (value) => {
  if (!value && value !== 0) return "";
  return Number(value).toLocaleString();
};

export default function useAccountCorporateCardData() {
  // ========================= 공통 로딩(카운터 방식) =========================
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef(0);

  const withLoading = useCallback(async (fn) => {
    pendingRef.current += 1;
    setLoading(true);
    try {
      return await fn();
    } finally {
      pendingRef.current -= 1;
      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        setLoading(false);
      }
    }
  }, []);

  // ========================= 법인카드관리(모달) =========================
  const [activeRows, setActiveRows] = useState([]); // 카드 목록

  // ========================= 결제내역(상단) =========================
  const [paymentRows, setPaymentRows] = useState([]);

  // ========================= 결제상세(하단) =========================
  const [paymentDetailRows, setPaymentDetailRows] = useState([]);

  // (옵션) 기존 유지
  const [corporateRows, setCorporateRows] = useState([]);

  // 거래처 셀렉트용
  const [accountList, setAccountList] = useState([]);

  // ========================= (0) 거래처 목록 조회 =========================
  const fetchAccountList = useCallback(async () => {
    return withLoading(async () => {
      try {
        const res = await api.get("/Account/AccountList", {
          params: { account_type: 0 },
        });
        setAccountList(res.data || []);
      } catch (err) {
        console.error("AccountList 조회 실패:", err);
        setAccountList([]);
      }
    });
  }, [withLoading]);

  // ========================= (1) 법인카드관리 목록 조회 =========================
  const fetchAccountCorporateCardList = useCallback(
    async (account_id) => {
      return withLoading(async () => {
        try {
          const res = await api.get("/Account/AccountCorporateCardList", {
            params: { account_id },
          });

          const rows = (res.data || []).map((item) => ({
            idx: item.idx,
            account_id: item.account_id, // string/number 가능
            card_brand: item.card_brand,
            card_no: item.card_no,
            del_yn: item.del_yn ?? "N",
          }));

          setActiveRows(rows);
        } catch (err) {
          console.error("법인카드 목록 조회 실패:", err);
          setActiveRows([]);
        }
      });
    },
    [withLoading]
  );

  // ========================= (2) 결제내역(상단) 조회 =========================
  // ✅ account_id 추가
  const fetchAccountCorporateCardPaymentList = useCallback(
    async ({ year, month, account_id }) => {
      return withLoading(async () => {
        try {
          const res = await api.get("/Account/AccountCorporateCardPaymentList", {
            params: { year, month, account_id },
          });

          setPaymentRows(res.data || []);
        } catch (err) {
          console.error("결제내역 조회 실패:", err);
          setPaymentRows([]);
        }
      });
    },
    [withLoading]
  );

  // ========================= (3) 결제상세(하단) 조회 =========================
  const fetchAccountCorporateCardPaymentDetailList = useCallback(
    async ({ sale_id, account_id, payment_dt }) => {
      return withLoading(async () => {
        try {
          const res = await api.get("/Account/AccountCorporateCardPaymentDetailList", {
            params: { sale_id, account_id, payment_dt },
          });

          setPaymentDetailRows(res.data || []);
        } catch (err) {
          console.error("결제상세 조회 실패:", err);
          setPaymentDetailRows([]);
        }
      });
    },
    [withLoading]
  );

  // ========================= (4) (옵션) 기존 corporateRows 조회 =========================
  const fetchCorporateCardPayList = useCallback(
    async (account_id) => {
      return withLoading(async () => {
        try {
          const res = await api.get("/Account/AccountCorporateCardList", {
            params: { account_id },
          });

          const rows = (res.data || []).map((item) => ({
            idx: item.idx,
            account_id: item.account_id,
            card_brand: item.card_brand,
            card_no: item.card_no,
            del_yn: item.del_yn ?? "N",
          }));

          setCorporateRows(rows);
        } catch (err) {
          console.error("데이터 조회 실패:", err);
          setCorporateRows([]);
        }
      });
    },
    [withLoading]
  );

  const deleteAccountCorporateCardPayment = useCallback(async (payload) => {
    return api.post("/Account/AccountCorporateCardPaymentDelete", payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true,
    });
  }, []);

  const deleteAccountCorporateCardPaymentDetail = useCallback(async (payload) => {
    return api.post("/Account/AccountCorporateCardPaymentDetailDelete", payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true,
    });
  }, []);

  const parseAccountCorporateCardReceipt = useCallback(async (formData) => {
    return api.post("/card-receipt/parse", formData, {
      headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
      validateStatus: () => true,
    });
  }, []);

  const saveAccountCorporateCardPaymentAll = useCallback(async (payload) => {
    return api.post("/Account/AccountCorporateCardPaymentAllSave", payload, {
      headers: { "Content-Type": "application/json" },
    });
  }, []);

  const saveAccountCorporateCard = useCallback(async (payload) => {
    return api.post("/Account/AccountCorporateCardSave", payload, {
      headers: { "Content-Type": "application/json" },
    });
  }, []);

  return {
    // state
    loading,
    setLoading,
    withLoading,

    activeRows,
    setActiveRows,

    paymentRows,
    setPaymentRows,

    paymentDetailRows,
    setPaymentDetailRows,

    corporateRows,
    setCorporateRows,

    accountList,
    setAccountList,

    // fetchers
    fetchAccountList,
    fetchAccountCorporateCardList,
    fetchAccountCorporateCardPaymentList,
    fetchAccountCorporateCardPaymentDetailList,
    fetchCorporateCardPayList,
    deleteAccountCorporateCardPayment,
    deleteAccountCorporateCardPaymentDetail,
    parseAccountCorporateCardReceipt,
    saveAccountCorporateCardPaymentAll,
    saveAccountCorporateCard,
  };
}

export { parseNumber, formatNumber };
